import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { useCountdown } from "@/hooks/use-countdown"
import { cn } from "@/lib/utils"

interface CountdownHeroProps {
  dropsAt: string
  campaignLabel: string
  missionCount: number
  /** Fired once when the countdown reaches zero (page triggers the reload). */
  onComplete: () => void
  /** Slot below the subline — the Notify toggle pill lives here. */
  children?: ReactNode
  className?: string
}

/** Final-seconds window where the hero flips to the "DROPPING NOW" edge. */
const DROPPING_NOW_MS = 10_000

function pad(value: number): string {
  return String(value).padStart(2, "0")
}

/**
 * OKS-477 — §07 countdown hero (Figma `47:258`). The orange anticipation
 * block: eyebrow + large `HH : MM : SS` timer + "{campaign} · {N} missions
 * land {when}" subline. Ticks client-side (no per-second server hit) and in
 * the final {@link DROPPING_NOW_MS} flips to a "DROPPING NOW · 00:00:0X" edge
 * (UX Spec §1 edge).
 *
 * Accessibility (UX Spec §6): the visible timer is `aria-hidden` (a
 * per-second SR read would be spam); a sibling `aria-live="polite"` region
 * announces only at coarse minute intervals ("{N} minutes until the drop"),
 * then "Dropping now" once. The accent is conveyed by text + layout, never
 * colour alone.
 */
export function CountdownHero({
  dropsAt,
  campaignLabel,
  missionCount,
  onComplete,
  children,
  className,
}: CountdownHeroProps) {
  const { t, i18n } = useTranslation()
  const { hours, minutes, seconds, totalMs } = useCountdown(dropsAt, onComplete)

  const droppingNow = totalMs > 0 && totalMs <= DROPPING_NOW_MS
  // Coarse SR announcement — minutes remaining (rounded up), so the live
  // region text only changes once a minute instead of every second.
  const minutesRemaining = Math.ceil(totalMs / 60_000)

  const when = new Intl.DateTimeFormat(i18n.language, {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(dropsAt))

  return (
    <section
      className={cn(
        "rounded-2xl bg-[#ff5c00] px-5 py-6 text-white shadow-sm",
        className,
      )}
      data-testid="countdown-hero"
      aria-label={t("mission_drop.region_label")}
    >
      <p className="text-xs font-bold tracking-[0.12em] uppercase opacity-90">
        {droppingNow
          ? t("mission_drop.dropping_now")
          : t("mission_drop.eyebrow")}
      </p>

      {/* Visible ticking timer — hidden from SR (the live region below speaks). */}
      <p
        className="mt-3 font-mono text-4xl font-bold tabular-nums sm:text-5xl"
        data-testid="countdown-timer"
        aria-hidden="true"
      >
        {pad(hours)} : {pad(minutes)} : {pad(seconds)}
      </p>

      {/* Coarse, polite SR announcement (minute changes + the edge). */}
      <p className="sr-only" aria-live="polite" data-testid="countdown-sr">
        {droppingNow
          ? t("mission_drop.dropping_now_aria")
          : t("mission_drop.timer_remaining", { count: minutesRemaining })}
      </p>

      <p
        className="mt-3 text-sm font-medium opacity-95"
        data-testid="countdown-subline"
      >
        {t("mission_drop.subline", {
          count: missionCount,
          campaign: campaignLabel,
          when,
        })}
      </p>

      {children}
    </section>
  )
}
